import { useEffect, useMemo, useState } from 'react';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { Textarea } from '../../../components/ui/textarea';
import { Label } from '../../../components/ui/label';
import { Plus, Save, Trash2 } from 'lucide-react';
import { toast } from 'sonner';

const getCurrentMonth = () => new Date().toISOString().slice(0, 7);

export function MonthlyExpenseManager({ api, onChanged }) {
  const [billingMonth, setBillingMonth] = useState(getCurrentMonth());
  const [expenses, setExpenses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState({ name: '', amount: '', billing_month: getCurrentMonth(), category: '', notes: '' });

  const loadExpenses = async (month = billingMonth) => {
    setLoading(true);
    try {
      const res = await api.get('/api/accounting/monthly-expenses', { params: { billing_month: month } });
      setExpenses(res.data.expenses || []);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to load billing month expenses');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    setForm((prev) => ({ ...prev, billing_month: billingMonth }));
    loadExpenses(billingMonth);
  }, [billingMonth]);

  const resetForm = () => {
    setEditingId(null);
    setForm({ name: '', amount: '', billing_month: billingMonth, category: '', notes: '' });
  };

  const handleSave = async () => {
    if (!form.name || !form.amount) {
      toast.error('Name and amount are required');
      return;
    }

    const payload = {
      ...form,
      amount: parseFloat(form.amount) || 0,
      billing_month: billingMonth,
    };

    try {
      if (editingId) {
        await api.put(`/api/accounting/monthly-expenses/${editingId}`, payload);
        toast.success('Monthly expense updated');
      } else {
        await api.post('/api/accounting/monthly-expenses', payload);
        toast.success('Monthly expense added');
      }
      resetForm();
      loadExpenses(billingMonth);
      if (onChanged) onChanged();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save monthly expense');
    }
  };

  const handleDelete = async (expenseId) => {
    if (!window.confirm('Delete this billing month expense?')) return;
    try {
      await api.delete(`/api/accounting/monthly-expenses/${expenseId}`);
      toast.success('Monthly expense deleted');
      loadExpenses(billingMonth);
      if (onChanged) onChanged();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete monthly expense');
    }
  };

  const total = useMemo(() => expenses.reduce((sum, item) => sum + (item.amount || 0), 0), [expenses]);

  return (
    <div className="bg-white rounded-xl border border-gray-200 p-6" data-testid="monthly-expense-manager">
      <div className="flex items-center justify-between mb-5 gap-4 flex-wrap">
        <div>
          <h3 className="text-lg font-semibold text-[#0A1628]">Billing Month Expense List</h3>
          <p className="text-sm text-gray-500">These expenses are subtracted before accounting totals and partner splits.</p>
        </div>
        <div className="flex items-center gap-3">
          <Label className="text-sm text-gray-500">Billing Month</Label>
          <Input type="month" value={billingMonth} onChange={(e) => setBillingMonth(e.target.value)} data-testid="billing-month-picker" />
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-[360px_minmax(0,1fr)] gap-6">
        <div className="rounded-xl border border-gray-200 p-4 bg-gray-50 space-y-4">
          <div>
            <Label className="text-xs text-gray-500">Expense Name</Label>
            <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="mt-1" data-testid="monthly-expense-name-input" />
          </div>
          <div>
            <Label className="text-xs text-gray-500">Amount</Label>
            <Input type="number" step="0.01" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} className="mt-1" data-testid="monthly-expense-amount-input" />
          </div>
          <div>
            <Label className="text-xs text-gray-500">Category</Label>
            <Input value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} className="mt-1" placeholder="e.g. payroll, software, ads" />
          </div>
          <div>
            <Label className="text-xs text-gray-500">Notes</Label>
            <Textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className="mt-1" rows={3} />
          </div>
          <div className="flex gap-2">
            <Button onClick={handleSave} className="bg-[#014DB7]" data-testid="save-monthly-expense-button"><Save className="w-4 h-4 mr-2" /> {editingId ? 'Update' : 'Add Expense'}</Button>
            {editingId && <Button variant="outline" onClick={resetForm}>Cancel</Button>}
          </div>
        </div>

        <div className="rounded-xl border border-gray-200 overflow-hidden">
          <div className="px-4 py-3 bg-gray-50 border-b border-gray-200 flex items-center justify-between">
            <div className="font-semibold text-[#0A1628]">{billingMonth} Expenses</div>
            <div className="text-sm text-gray-500">Total: {new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(total)}</div>
          </div>
          {loading ? (
            <div className="p-10 text-center text-gray-500">Loading expenses…</div>
          ) : expenses.length === 0 ? (
            <div className="p-10 text-center text-gray-500" data-testid="monthly-expense-empty-state"><Plus className="w-10 h-10 text-gray-300 mx-auto mb-3" />No expenses entered for this billing month</div>
          ) : (
            <div className="divide-y divide-gray-100">
              {expenses.map((expense) => (
                <div key={expense.id} className="p-4 flex items-center justify-between gap-4" data-testid={`monthly-expense-row-${expense.id}`}>
                  <div className="min-w-0">
                    <p className="font-medium text-[#0A1628] truncate">{expense.name}</p>
                    <p className="text-xs text-gray-500 mt-1">{expense.category || 'other'} {expense.notes ? `• ${expense.notes}` : ''}</p>
                  </div>
                  <div className="flex items-center gap-3">
                    <span className="font-semibold text-gray-900">{new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(expense.amount || 0)}</span>
                    <Button variant="outline" size="sm" onClick={() => { setEditingId(expense.id); setForm({ name: expense.name, amount: String(expense.amount || ''), billing_month: billingMonth, category: expense.category || '', notes: expense.notes || '' }); }}>Edit</Button>
                    <Button variant="outline" size="sm" className="text-red-600 border-red-200 hover:bg-red-50" onClick={() => handleDelete(expense.id)}><Trash2 className="w-4 h-4" /></Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}